import React, { useState, useRef } from 'react';
import { XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';

interface UploadedImage {
  id: string;
  file: File;
  preview: string;
  progress: number;
  uploaded: boolean;
  s3Key?: string;
}

interface MultipleImageUploaderProps {
  onUpload: (file: File, onProgress?: (progress: number) => void) => Promise<string>; // Returns S3 key
  onImagesChange: (s3Keys: string[]) => void;
  label: string;
  maxFiles?: number;
  maxSize?: number; // in MB
  helperText?: string;
}

export const MultipleImageUploader: React.FC<MultipleImageUploaderProps> = ({
  onUpload,
  onImagesChange,
  label,
  maxFiles = 5,
  maxSize = 5,
  helperText,
}) => {
  const [images, setImages] = useState<UploadedImage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    
    if (images.length + files.length > maxFiles) {
      setError(`Maximum ${maxFiles} images allowed`);
      return;
    }

    setError(null);

    const newImages: UploadedImage[] = files.map((file) => ({
      id: Math.random().toString(36).substring(7),
      file,
      preview: URL.createObjectURL(file),
      progress: 0,
      uploaded: false,
    }));

    setImages((prev) => [...prev, ...newImages]);

    // Upload each file
    for (const image of newImages) {
      try {
        // Validate file
        if (image.file.size > maxSize * 1024 * 1024) {
          setError(`${image.file.name} is too large (max ${maxSize}MB)`);
          continue;
        }

        // Upload with real progress tracking
        const s3Key = await onUpload(image.file, (progress) => {
          setImages((prev) =>
            prev.map((img) =>
              img.id === image.id
                ? { ...img, progress }
                : img
            )
          );
        });

        // Mark as uploaded
        setImages((prev) =>
          prev.map((img) =>
            img.id === image.id
              ? { ...img, progress: 100, uploaded: true, s3Key }
              : img
          )
        );

        // Update parent with all S3 keys
        setTimeout(() => {
          setImages((current) => {
            const uploadedKeys = current
              .filter((img) => img.uploaded && img.s3Key)
              .map((img) => img.s3Key!);
            onImagesChange(uploadedKeys);
            return current;
          });
        }, 100);
      } catch (err) {
        setImages((prev) => prev.filter((img) => img.id !== image.id));
        setError(`Failed to upload ${image.file.name}`);
      }
    }

    // Clear input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemove = (id: string) => {
    setImages((prev) => {
      const updated = prev.filter((img) => img.id !== id);
      const uploadedKeys = updated
        .filter((img) => img.uploaded && img.s3Key)
        .map((img) => img.s3Key!);
      onImagesChange(uploadedKeys);
      return updated;
    });
  };

  const handleClick = () => {
    if (images.length >= maxFiles) {
      setError(`Maximum ${maxFiles} images allowed`);
      return;
    }
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-secondary-700">
        {label}
      </label>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {images.map((image) => (
          <div key={image.id} className="relative group aspect-square">
            <div className="w-full h-full rounded-lg overflow-hidden border-2 border-secondary-200 bg-secondary-50">
              <img
                src={image.preview}
                alt="Preview"
                className="w-full h-full object-cover"
              />
              {!image.uploaded && (
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-8 h-8 border-3 border-white border-t-transparent rounded-full animate-spin mx-auto mb-1"></div>
                    <p className="text-white text-xs font-medium">{image.progress}%</p>
                  </div>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => handleRemove(image.id)}
              className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-600"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        ))}

        {images.length < maxFiles && (
          <button
            type="button"
            onClick={handleClick}
            className="aspect-square border-2 border-dashed border-secondary-300 rounded-lg hover:border-primary-500 transition-colors duration-200 bg-secondary-50 hover:bg-primary-50 flex flex-col items-center justify-center"
          >
            <PlusIcon className="h-8 w-8 text-secondary-400 mb-2" />
            <p className="text-xs font-medium text-secondary-600">Add Image</p>
          </button>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      {helperText && !error && (
        <p className="text-xs text-secondary-500">{helperText}</p>
      )}

      {error && (
        <p className="text-xs text-red-500">{error}</p>
      )}

      <p className="text-xs text-secondary-500">
        {images.length} / {maxFiles} images uploaded
      </p>
    </div>
  );
};

